import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { BiData, FeedSortOrderSelectBiEvent } from '@wix/da-bi/pkg/events';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import {
  getIsReorderSupported,
  getSelectedFolder,
} from '../../../selectors/studio';
import { usePageOffsets } from '../../../hooks/usePageOffsets';
import { reorderRow as reorderRowAction } from '../../../actions/studio';

export function useReorderAction(
  deviation: PapiDeviation,
  offset: number
): NullableDropdownItem {
  const { t } = useTranslation();

  const selectedFolder = useSelector(getSelectedFolder)!;
  const isReorderSupported = useSelector(getIsReorderSupported);
  const reorderRow = useAction(reorderRowAction);
  const { topOfPage } = usePageOffsets(selectedFolder);

  const onReorder = useCallback(
    (position: number) =>
      reorderRow(selectedFolder.folderId, deviation.deviationId, position),
    [reorderRow, selectedFolder.folderId, deviation.deviationId]
  );

  const onOrderSelect = useCallback(
    value => {
      if (!selectedFolder || typeof offset === 'undefined') {
        return;
      }

      let newPosition;
      switch (value) {
        case 'up':
          newPosition = topOfPage + offset - 1;
          break;

        case 'down':
          newPosition = topOfPage + offset + 1;
          break;

        case 'top':
          newPosition = 0;
          break;

        case 'bottom':
          newPosition = selectedFolder.size - 1;
          break;

        default:
          return;
      }

      onReorder(newPosition);
    },
    [selectedFolder, offset, topOfPage, onReorder]
  );

  const dropdownItemBiData = useCallback(
    value =>
      BiData<FeedSortOrderSelectBiEvent>({
        evid: 403,
        itemid: deviation.deviationId,
        typeid: getDeviationResourceType(deviation),
        sort_order: value,
      }),
    [deviation]
  );

  return (
    selectedFolder &&
    selectedFolder.size > 1 &&
    isReorderSupported && {
      label: t('studio.table.actions.order'),
      submenu: (
        <StandardMenu
          id={`reorder-${deviation.deviationId}`}
          items={[
            {
              label: t('studio.table.actions.order.top'),
              value: 'top',
              biData: dropdownItemBiData('top'),
              disabled: topOfPage + offset === 0,
            },
            {
              label: t('studio.table.actions.order.bottom'),
              value: 'bottom',
              biData: dropdownItemBiData('bottom'),
              disabled: topOfPage + offset === selectedFolder.size - 1,
            },
            {
              label: t('studio.table.actions.order.up'),
              value: 'up',
              biData: dropdownItemBiData('up'),
              disabled: topOfPage + offset === 0,
            },
            {
              label: t('studio.table.actions.order.down'),
              value: 'down',
              biData: dropdownItemBiData('down'),
              disabled: topOfPage + offset === selectedFolder.size - 1,
            },
          ]}
          onSelect={onOrderSelect}
        />
      ),
      subMenuPreferredSide: 'right',
      estimatedSubmenuWidth: 200,
    }
  );
}
